import { REPLACE_NEEDS_DELETE_REASON } from "../components/BpaImport/importText";
import { languageName } from "./languageName";
import {
  type ClashChoice,
  type ImportItem,
  type ImportKind,
  type ImportPageState,
  type ImportPreview,
  type ImportPreviewOrg,
  type ImportSelection,
  type ImportSummary,
  type ItemChoice,
  firmKey,
  KIND_OF_ITEM,
  slotsInOrder,
} from "./bpaImport";

export const LEFT_OUT_FIRM_REASON =
  "This organisation is not ticked, so none of its content is imported. Tick it to change what happens to it.";

export type SwitchPosition = "bring" | "leave" | "keep" | "replace" | "remove";

export type FirmControl = {
  choice: ClashChoice;
  itemChoices: Record<string, ItemChoice>;
  clicked: string[];
  leftOut?: boolean;
};

type Side = "source" | "target";
type Entry = { item: ImportItem; side: Side };

const SLOT_WINNERS: ItemChoice[] = ["incoming", "existing"];
const INCOMING_ONLY_CHOICES: ItemChoice[] = ["bring", "leave"];
const EXISTING_ONLY_CHOICES: ItemChoice[] = ["keep", "remove"];

export function firmControl(state: ImportPageState, key: string): FirmControl {
  return {
    choice: state.choices[key] ?? "keep_target",
    itemChoices: state.itemChoices?.[key] ?? {},
    clicked: state.clicked?.[key] ?? [],
    ...(state.unticked.includes(key) ? { leftOut: true } : {}),
  };
}

function withoutKey<T>(record: Record<string, T>, key: string): Record<string, T> {
  const { [key]: _removed, ...rest } = record;
  return rest;
}

function withEntry<T>(
  record: Record<string, T> | undefined,
  key: string,
  value: T,
  isEmpty: boolean,
): Record<string, T> {
  const rest = withoutKey(record ?? {}, key);
  return isEmpty ? rest : { ...rest, [key]: value };
}

export function withFirmControl(
  state: ImportPageState,
  key: string,
  control: FirmControl,
): ImportPageState {
  return {
    ...state,
    choices: { ...state.choices, [key]: control.choice },
    itemChoices: withEntry(
      state.itemChoices,
      key,
      control.itemChoices,
      Object.keys(control.itemChoices).length === 0,
    ),
    clicked: withEntry(state.clicked, key, control.clicked, control.clicked.length === 0),
  };
}

function entriesOf(org: ImportPreviewOrg): Entry[] {
  const source = (org.items?.source ?? []).map(item => ({ item, side: "source" as const }));
  const target = (org.items?.target ?? []).map(item => ({ item, side: "target" as const }));
  return [...source, ...target];
}

function presetPosition(entry: Entry, choice: ClashChoice): SwitchPosition {
  const outcome = entry.item.outcomes[choice];
  if (entry.side === "source") return outcome === "new" ? "bring" : "leave";
  if (outcome === "replaced") return "replace";
  if (outcome === "removed") return "remove";
  return "keep";
}

function choiceFitsEntry(entry: Entry, choice: ItemChoice): boolean {
  if (entry.item.sharedSlot) return SLOT_WINNERS.includes(choice);
  const allowed = entry.side === "source" ? INCOMING_ONLY_CHOICES : EXISTING_ONLY_CHOICES;
  return allowed.includes(choice);
}

function slotEntriesOf(entries: Entry[], entry: Entry): Entry[] {
  return entries.filter(other => other.item.slot === entry.item.slot);
}

function storedChoiceInSlot(
  slotEntries: Entry[],
  itemChoices: Record<string, ItemChoice>,
): { choice: ItemChoice; entry: Entry } | null {
  for (const entry of slotEntries) {
    const choice = itemChoices[entry.item.id];
    if (choice !== undefined && choiceFitsEntry(entry, choice)) return { choice, entry };
  }
  return null;
}

function positionFromChoice(entry: Entry, choice: ItemChoice): SwitchPosition {
  if (choice === "incoming") return entry.side === "source" ? "bring" : "replace";
  if (choice === "existing") return entry.side === "source" ? "leave" : "keep";
  return choice;
}

function positionOf(entries: Entry[], entry: Entry, control: FirmControl): SwitchPosition {
  const stored = storedChoiceInSlot(slotEntriesOf(entries, entry), control.itemChoices);
  if (!stored) return presetPosition(entry, control.choice);
  return positionFromChoice(entry, stored.choice);
}

export function switchPositions(
  org: ImportPreviewOrg,
  control: FirmControl,
): Record<string, SwitchPosition> {
  const entries = entriesOf(org);
  const positions: Record<string, SwitchPosition> = {};
  for (const entry of entries) positions[entry.item.id] = positionOf(entries, entry, control);
  return positions;
}

function choiceForPosition(entry: Entry, to: SwitchPosition): ItemChoice | null {
  if (entry.item.sharedSlot) {
    if (entry.side === "source" && to === "bring") return "incoming";
    if (entry.side === "target" && to === "replace") return "incoming";
    if (to === "leave") return entry.side === "source" ? "existing" : null;
    if (to === "keep") return entry.side === "target" ? "existing" : null;
    return null;
  }
  if (entry.side === "source") return to === "bring" || to === "leave" ? to : null;
  return to === "keep" || to === "remove" ? to : null;
}

export function toggleItem(
  org: ImportPreviewOrg,
  control: FirmControl,
  itemId: string,
  to: SwitchPosition,
): FirmControl {
  const entries = entriesOf(org);
  const entry = entries.find(candidate => candidate.item.id === itemId);
  if (!entry) return control;
  const choice = choiceForPosition(entry, to);
  if (choice === null) return control;
  const slotIds = slotEntriesOf(entries, entry).map(member => member.item.id);
  const itemChoices = Object.fromEntries(
    Object.entries(control.itemChoices).filter(([id]) => !slotIds.includes(id)),
  );
  const clicked = control.clicked.filter(id => !slotIds.includes(id));
  if (to === presetPosition(entry, control.choice)) {
    return { choice: control.choice, itemChoices, clicked };
  }
  return {
    choice: control.choice,
    itemChoices: { ...itemChoices, [itemId]: choice },
    clicked: [...clicked, itemId],
  };
}

export function applyPreset(_control: FirmControl, choice: ClashChoice): FirmControl {
  return { choice, itemChoices: {}, clicked: [] };
}

export function resetFirm(control: FirmControl): FirmControl {
  return applyPreset(control, control.choice);
}

export function changedItemIds(org: ImportPreviewOrg, control: FirmControl): string[] {
  const entries = entriesOf(org);
  return control.clicked.filter(id => {
    const entry = entries.find(candidate => candidate.item.id === id);
    if (!entry) return false;
    return positionOf(entries, entry, control) !== presetPosition(entry, control.choice);
  });
}

export function changeCount(org: ImportPreviewOrg, control: FirmControl): number {
  return changedItemIds(org, control).length;
}

function hasStoredChanges(control: FirmControl): boolean {
  return Object.keys(control.itemChoices).length > 0 || control.clicked.length > 0;
}

function languageNameOf(item: ImportItem): string {
  return item.languageIso ? `${languageName(item.languageIso)} ` : "";
}

function spansSeveralLanguages(entries: Entry[]): boolean {
  const languages = new Set<string>();
  for (const { item } of entries) {
    if (item.kind === "submission" && item.languageIso) languages.add(item.languageIso);
  }
  return languages.size > 1;
}

function nounOf(item: ImportItem, withTier: boolean, withLanguage = false): string {
  if (item.kind === "submission") return `${withLanguage ? languageNameOf(item) : ""}submission`;
  if (item.kind === "commentary") return "commentary";
  return withTier && item.tier !== null ? `Tier ${item.tier}` : "ranking";
}

function leadOf(item: ImportItem, side: Side): string {
  return item.contactName ? `${item.contactName}'s ${side}` : `the ${side}'s`;
}

function joinClauses(clauses: string[]): string {
  if (clauses.length <= 1) return clauses.join("");
  return `${clauses.slice(0, -1).join(", ")} and ${clauses[clauses.length - 1]}`;
}

function sharedSlotClause(
  incoming: ImportItem,
  existing: ImportItem,
  positions: Record<string, SwitchPosition>,
  withLanguage: boolean,
): string {
  if (positions[incoming.id] !== "bring") {
    return `${leadOf(existing, "target")} ${nounOf(existing, true, withLanguage)} stays`;
  }
  const replaced = incoming.kind === "firm_ranking" || incoming.kind === "lawyer_ranking";
  const targetNoun = replaced ? nounOf(existing, true) : "one";
  return `${leadOf(incoming, "source")} ${nounOf(incoming, true, withLanguage)} replaces the target's ${targetNoun}`;
}

function submittedOn(item: ImportItem): string {
  if (item.kind !== "submission" || !item.submittedAt) return "";
  const day = new Date(item.submittedAt).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
  return ` (${day})`;
}

function sourceOnes(bringing: Entry[]): string {
  return bringing.length === 1 ? "the source's one" : `the source's ${bringing.length}`;
}

function targetEnding(position: SwitchPosition, bringing: Entry[]): string {
  if (position === "remove") return "is removed";
  if (position !== "replace") return "stays";
  return bringing.length === 0 ? "is replaced" : `is replaced by ${sourceOnes(bringing)}`;
}

type Piece = { outcome: string; side: Side; noun: string; clause: string; item: ImportItem };

type Element = {
  outcome: string;
  side: Side;
  owner: string | null;
  nouns: string[];
  clause: string | null;
  rank: number;
  who: string;
  kind: string;
  plain: boolean;
};

const FIRM_RANK = 0;
const COUNTED_RANK = 1;
const NAMED_RANK = 2;
const STAYS = "stays";
const REPLACES = "replaces";

function sharedSlotPiece(
  incoming: Entry,
  existing: Entry,
  positions: Record<string, SwitchPosition>,
  withLanguage: boolean,
): Piece {
  const clause = sharedSlotClause(incoming.item, existing.item, positions, withLanguage);
  if (positions[incoming.item.id] !== "bring") {
    const noun = nounOf(existing.item, true, withLanguage);
    return { outcome: STAYS, side: "target", noun, clause, item: existing.item };
  }
  const noun = nounOf(incoming.item, true, withLanguage);
  return { outcome: REPLACES, side: "source", noun, clause, item: incoming.item };
}

function unpairedPieces(
  members: Entry[],
  positions: Record<string, SwitchPosition>,
  withLanguage: boolean,
): Piece[] {
  const countOnSide = (side: Side) => members.filter(member => member.side === side).length;
  const apart = countOnSide("source") > 1 || countOnSide("target") > 1;
  const bringing = members.filter(
    member => member.side === "source" && positions[member.item.id] === "bring",
  );
  return members.map(entry => {
    const { item, side } = entry;
    const position = positions[item.id];
    const noun = `${nounOf(item, apart, withLanguage)}${apart ? submittedOn(item) : ""}`;
    const sourceEnding = position === "bring" ? "is added" : "is left out";
    const outcome = side === "target" ? targetEnding(position, bringing) : sourceEnding;
    return { outcome, side, noun, clause: `${leadOf(item, side)} ${noun} ${outcome}`, item };
  });
}

function slotPieces(
  members: Entry[],
  positions: Record<string, SwitchPosition>,
  withLanguage: boolean,
): Piece[] {
  const incoming = members.filter(member => member.side === "source");
  const existing = members.filter(member => member.side === "target");
  if (incoming.length === 1 && existing.length === 1 && incoming[0].item.sharedSlot) {
    return [sharedSlotPiece(incoming[0], existing[0], positions, withLanguage)];
  }
  return unpairedPieces(members, positions, withLanguage);
}

function isLawyerPiece(piece: Piece): boolean {
  return Boolean(piece.item.contactName);
}

function firmElement(piece: Piece): Element {
  return {
    outcome: piece.outcome,
    side: piece.side,
    owner: null,
    nouns: [piece.noun],
    clause: piece.clause,
    rank: FIRM_RANK,
    who: "",
    kind: "",
    plain: true,
  };
}

function lawyerKindOf(piece: Piece): string {
  return piece.item.kind === "submission" ? "submission" : "ranking";
}

function namedElement(piece: Piece): Element {
  return {
    ...firmElement(piece),
    owner: `${piece.item.contactName}'s`,
    rank: NAMED_RANK,
    who: piece.item.contactId ?? piece.item.contactName ?? "",
    kind: lawyerKindOf(piece),
  };
}

function countedElement(piece: Piece, count: number, withLanguage: boolean): Element {
  const noun = nounOf(piece.item, false, withLanguage);
  return {
    ...firmElement(piece),
    owner: `${count} lawyers'`,
    nouns: [`${noun}s`],
    clause: null,
    rank: COUNTED_RANK,
    who: String(count),
    kind: lawyerKindOf(piece),
    plain: noun === nounOf(piece.item, false, false),
  };
}

function groupKeyOf(piece: Piece, withLanguage: boolean): string {
  return `${piece.outcome}|${nounOf(piece.item, false, withLanguage)}`;
}

function largestGroupKey(keys: string[]): string | null {
  const sizes = new Map<string, number>();
  for (const key of keys) sizes.set(key, (sizes.get(key) ?? 0) + 1);
  let largest: string | null = null;
  let largestSize = 1;
  for (const [key, size] of sizes) {
    if (size > largestSize) {
      largest = key;
      largestSize = size;
    }
  }
  return largest;
}

function lawyerKindElements(
  slots: Entry[][],
  positions: Record<string, SwitchPosition>,
  withLanguage: boolean,
): Element[] {
  const piecesBySlot = slots.map(members => slotPieces(members, positions, withLanguage));
  const singles = piecesBySlot.filter(pieces => pieces.length === 1).map(([piece]) => piece);
  const counted = largestGroupKey(singles.map(piece => groupKeyOf(piece, withLanguage)));
  const isCounted = (piece: Piece) =>
    counted !== null &&
    piece.item.contactName !== null &&
    groupKeyOf(piece, withLanguage) === counted;
  const inGroup = piecesBySlot.filter(pieces => pieces.length === 1 && isCounted(pieces[0]));
  const named = piecesBySlot.filter(pieces => pieces.length !== 1 || !isCounted(pieces[0])).flat();
  const countElement =
    inGroup.length === 0 ? [] : [countedElement(inGroup[0][0], inGroup.length, withLanguage)];
  return [...countElement, ...named.map(namedElement)];
}

function inLawyerOrder(elements: Element[]): Element[] {
  const order = new Map<string, number>();
  for (const element of elements) {
    if (element.rank === NAMED_RANK && !order.has(element.who)) order.set(element.who, order.size);
  }
  const position = (element: Element) => order.get(element.who) ?? 0;
  return [...elements].sort(
    (first, second) => first.rank - second.rank || position(first) - position(second),
  );
}

function canMerge(previous: Element, next: Element): boolean {
  return (
    previous.rank > FIRM_RANK &&
    previous.rank === next.rank &&
    previous.who === next.who &&
    previous.kind !== next.kind &&
    previous.plain &&
    next.plain
  );
}

function mergeNeighbours(elements: Element[]): Element[] {
  const merged: Element[] = [];
  for (const element of elements) {
    const previous = merged[merged.length - 1];
    if (previous && canMerge(previous, element)) {
      merged[merged.length - 1] = {
        ...previous,
        nouns: [...previous.nouns, ...element.nouns],
        clause: null,
        kind: element.kind,
      };
    } else {
      merged.push(element);
    }
  }
  return merged;
}

function groupedByOutcome(elements: Element[]): Element[][] {
  const groups = new Map<string, Element[]>();
  for (const element of elements) {
    groups.set(element.outcome, [...(groups.get(element.outcome) ?? []), element]);
  }
  return [...groups.values()].map(mergeNeighbours);
}

function pluralVerb(outcome: string): string {
  if (outcome === STAYS) return "stay";
  if (outcome === REPLACES) return "replace the target's";
  return outcome.replace(/^is /, "are ");
}

function elementText(element: Element, withSide: boolean): string {
  const nouns = element.nouns.join(" and ");
  if (element.owner !== null) {
    return `${element.owner} ${withSide ? `${element.side} ` : ""}${nouns}`;
  }
  return withSide ? `the ${element.side}'s ${nouns}` : nouns;
}

function groupText(group: Element[]): string {
  const [first] = group;
  if (group.length === 1 && first.clause !== null) return first.clause;
  const text = joinClauses(group.map((element, index) => elementText(element, index === 0)));
  return `${text} ${pluralVerb(first.outcome)}`;
}

function everythingStaysElementText(element: Element): string {
  const nouns = element.nouns.join(" and ");
  if (element.owner !== null) return `${element.owner} ${nouns}`;
  return /^Tier /.test(nouns) ? nouns : `the ${nouns}`;
}

function everythingStaysText(group: Element[]): string {
  const texts = group.map(everythingStaysElementText);
  if (texts.length === 2) return joinClauses(texts);
  return `${texts.slice(0, -1).join(", ")}, and ${texts[texts.length - 1]}`;
}

function staysEverything(groups: Element[][]): boolean {
  return groups.length === 1 && groups[0][0].outcome === STAYS && groups[0].length >= 2;
}

function sentenceBody(groups: Element[][]): string {
  if (staysEverything(groups))
    return `everything in the target stays: ${everythingStaysText(groups[0])}`;
  return groups.map(groupText).join("; ");
}

export function firmSentence(org: ImportPreviewOrg, control: FirmControl): string {
  const positions = switchPositions(org, control);
  const entries = entriesOf(org);
  const withLanguage = spansSeveralLanguages(entries);
  const slots = slotsInOrder(entries, entry => entry.item);
  const piecesOf = (members: Entry[]) => slotPieces(members, positions, withLanguage);
  const lawyerSlots = slots.filter(members => piecesOf(members).every(isLawyerPiece));
  const firmPieces = slots
    .filter(members => !lawyerSlots.includes(members))
    .flatMap(members => piecesOf(members));
  const isSubmissionSlot = (members: Entry[]) => members[0].item.kind === "submission";
  const elements = inLawyerOrder([
    ...firmPieces.map(firmElement),
    ...lawyerKindElements(lawyerSlots.filter(isSubmissionSlot), positions, withLanguage),
    ...lawyerKindElements(
      lawyerSlots.filter(members => !isSubmissionSlot(members)),
      positions,
      withLanguage,
    ),
  ]);
  const name = org.companyName ?? "No firm";
  if (elements.length === 0) return `For ${name}, nothing is imported.`;
  return `For ${name}, ${sentenceBody(groupedByOutcome(elements))}.`;
}

export function clickRowChoice(
  control: FirmControl,
  choice: ClashChoice,
): { control: FirmControl; undo: FirmControl | null } {
  if (choice === control.choice) return { control, undo: null };
  return {
    control: applyPreset(control, choice),
    undo: hasStoredChanges(control) ? control : null,
  };
}

function clashingKeys(orgs: ImportPreviewOrg[]): string[] {
  return orgs.filter(org => org.clash).map(firmKey);
}

export function applyBulkChoice(
  state: ImportPageState,
  orgs: ImportPreviewOrg[],
  choice: ClashChoice,
): { state: ImportPageState; undo: Record<string, FirmControl> | null } {
  const keys = clashingKeys(orgs);
  const hadChanges = keys.some(key => hasStoredChanges(firmControl(state, key)));
  let next = state;
  for (const key of keys)
    next = withFirmControl(next, key, applyPreset(firmControl(next, key), choice));
  const undo = Object.fromEntries(keys.map(key => [key, firmControl(state, key)]));
  return { state: next, undo: hadChanges ? undo : null };
}

export type UndoSnapshot = {
  before: Record<string, FirmControl>;
  after: Record<string, FirmControl>;
};

function sameControl(a: FirmControl, b: FirmControl): boolean {
  const normalised = (control: FirmControl) =>
    JSON.stringify([
      control.choice,
      Object.entries(control.itemChoices).sort(([x], [y]) => x.localeCompare(y)),
      [...control.clicked].sort(),
    ]);
  return normalised(a) === normalised(b);
}

function stillApplying(
  state: ImportPageState,
  org: ImportPreviewOrg | undefined,
  before: FirmControl,
): FirmControl {
  const itemChoices = Object.fromEntries(
    Object.entries(before.itemChoices).filter(([itemId, choice]) =>
      choiceStillApplies(org, state, itemId, choice),
    ),
  );
  return {
    choice: before.choice,
    itemChoices,
    clicked: before.clicked.filter(id => id in itemChoices),
  };
}

export function restorableControls(
  state: ImportPageState,
  snapshot: UndoSnapshot,
  orgs: ImportPreviewOrg[],
): Record<string, FirmControl> {
  const restorable: Record<string, FirmControl> = {};
  for (const [key, before] of Object.entries(snapshot.before)) {
    const current = firmControl(state, key);
    if (current.leftOut || !sameControl(current, snapshot.after[key])) continue;
    const restored = stillApplying(
      state,
      orgs.find(org => firmKey(org) === key),
      before,
    );
    if (!sameControl(restored, current)) restorable[key] = restored;
  }
  return restorable;
}

export function restoreFirmControls(
  state: ImportPageState,
  controls: Record<string, FirmControl>,
): ImportPageState {
  return Object.entries(controls).reduce(
    (next, [key, control]) => withFirmControl(next, key, control),
    state,
  );
}

export function bulkChoiceOffered(orgs: ImportPreviewOrg[]): boolean {
  return orgs.some(org => org.clash);
}

export function replacesExistingItem(org: ImportPreviewOrg): boolean {
  return entriesOf(org).some(entry => entry.item.sharedSlot);
}

export function replaceAllDisabledReason(
  orgs: ImportPreviewOrg[],
  canDelete: boolean,
): string | null {
  if (canDelete) return null;
  const wouldRemove = orgs.some(org => org.clash && replacesExistingItem(org));
  return wouldRemove ? REPLACE_NEEDS_DELETE_REASON : null;
}

export function switchDisabledReason(
  org: ImportPreviewOrg,
  itemId: string,
  position: SwitchPosition,
  canDelete: boolean,
): string | null {
  if (canDelete) return null;
  if (position === "replace" || position === "remove") return REPLACE_NEEDS_DELETE_REASON;
  if (position !== "bring") return null;
  const entry = entriesOf(org).find(candidate => candidate.item.id === itemId);
  return entry?.item.sharedSlot ? REPLACE_NEEDS_DELETE_REASON : null;
}

export function previewTotals(preview: ImportPreview, state: ImportPageState): ImportSummary {
  return previewOrgTotals(preview.orgs, state);
}

export function previewOrgTotals(orgs: ImportPreviewOrg[], state: ImportPageState): ImportSummary {
  const counts = { submissions: 0, rankings: 0, commentary: 0 };
  let organisations = 0;
  for (const org of orgs) {
    if (state.unticked.includes(firmKey(org))) continue;
    let brought = 0;
    for (const kind of state.kinds) {
      counts[kind] += org.importCounts[kind];
      brought += org.importCounts[kind];
    }
    if (brought > 0) organisations += 1;
  }
  return { organisations, counts };
}

function choiceStillApplies(
  org: ImportPreviewOrg | undefined,
  state: ImportPageState,
  itemId: string,
  choice: ItemChoice,
): boolean {
  if (!org || !org.clash || state.unticked.includes(firmKey(org))) return false;
  const entry = entriesOf(org).find(candidate => candidate.item.id === itemId);
  if (!entry) return false;
  if (!state.kinds.includes(KIND_OF_ITEM[entry.item.kind])) return false;
  return choiceFitsEntry(entry, choice);
}

export function dropStaleChoices(
  state: ImportPageState,
  preview: ImportPreview,
): { state: ImportPageState; dropped: number } {
  const itemChoices: Record<string, Record<string, ItemChoice>> = {};
  const clicked: Record<string, string[]> = {};
  let dropped = 0;
  for (const [key, choices] of Object.entries(state.itemChoices ?? {})) {
    const org = preview.orgs.find(candidate => firmKey(candidate) === key);
    const kept: Record<string, ItemChoice> = {};
    for (const [itemId, choice] of Object.entries(choices)) {
      if (choiceStillApplies(org, state, itemId, choice)) kept[itemId] = choice;
      else dropped += 1;
    }
    if (Object.keys(kept).length === 0) continue;
    itemChoices[key] = kept;
    clicked[key] = (state.clicked?.[key] ?? []).filter(id => id in kept);
  }
  return { state: { ...state, itemChoices, clicked }, dropped };
}

export function staleChoicesMessage(dropped: number): string | null {
  if (dropped <= 0) return null;
  if (dropped === 1) return "1 change no longer applies";
  return `${dropped} changes no longer apply`;
}

const FIRM_ID_IN_BRACKETS = /\(([^()]+)\)\s*$/;

function mentions(text: string, id: string): boolean {
  const escaped = id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|[^\\w-])${escaped}($|[^\\w-])`).test(text);
}

function onlyOne(matches: string[]): string | null {
  return matches.length === 1 ? matches[0] : null;
}

function firmNamedInDetails(named: string | null, firmKeys: string[]): string | null {
  if (named === null) return null;
  const exact = firmKeys.find(key => key === named);
  if (exact) return exact;
  const inBrackets = named.match(FIRM_ID_IN_BRACKETS)?.[1]?.trim();
  return firmKeys.find(key => key === inBrackets) ?? null;
}

function firmNamedInMessage(
  message: string,
  itemChoices: Record<string, Record<string, ItemChoice>>,
): string | null {
  const firmKeys = Object.keys(itemChoices);
  return (
    onlyOne(firmKeys.filter(key => mentions(message, key))) ??
    onlyOne(firmKeys.filter(key => Object.keys(itemChoices[key]).some(id => mentions(message, id))))
  );
}

export function readItemChoiceRefusal(
  error: unknown,
  selection: ImportSelection,
): { reason: string; companyId: string | null } | null {
  const { status, message, detailsRaw } = (error ?? {}) as {
    status?: number;
    message?: string;
    detailsRaw?: { companyId?: unknown };
  };
  if (status !== 400 || typeof message !== "string") return null;
  const itemChoices = selection.itemChoices ?? {};
  const firmKeys = Object.keys(itemChoices);
  if (firmKeys.length === 0) return null;
  const named = typeof detailsRaw?.companyId === "string" ? detailsRaw.companyId : null;
  const companyId = firmNamedInDetails(named, firmKeys) ?? firmNamedInMessage(message, itemChoices);
  return { reason: message, companyId };
}

export function resetRefusedChoices(
  state: ImportPageState,
  refusal: { companyId: string | null },
): ImportPageState {
  if (refusal.companyId === null) return { ...state, itemChoices: {}, clicked: {} };
  return {
    ...state,
    itemChoices: withoutKey(state.itemChoices ?? {}, refusal.companyId),
    clicked: withoutKey(state.clicked ?? {}, refusal.companyId),
  };
}
